import axios from 'axios';

// Define response interfaces
export interface Service {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface GetServicesResponse {
  status: 'success' | 'error';
  services: Service[];
  error?: string;
}

// All entity types derivable from the 4th character of an Indian PAN
export type ClientType =
  | 'INDIVIDUAL'
  | 'COMPANY'
  | 'HUF'
  | 'FIRM'
  | 'AOP'
  | 'TRUST'
  | 'BOI'
  | 'LOCAL_AUTHORITY'
  | 'AJP'
  | 'GOVERNMENT';

export interface Client {
  id: string;
  name: string | null;
  panId: string;
  email?: string | null;
  password: string;
  updatedAt: string;
  phone?: string | null;
  clientType?: ClientType;
  businessType?: 'AUDITED' | 'NON_AUDITED';
  loginStatus?: 'SUCCESSFUL' | 'INCORRECT_USERNAME' | 'INCORRECT_PASSWORD' | 'PAN_NOT_EXISTING' | null;
  isVerified?: boolean;
  dateOfBirth?: string | null;
  gender?: string | null;
  panStatus?: string | null;
  aadhaarLinked?: boolean | null;
  address?: string | null;
  profileLastSyncedAt?: string | null;
}
export interface RefreshStatus {
  latestSuccessDate: string | null;    // Most recent success date across all job types (null if no job ever succeeded)
  lastFailedAttempt: string | null;    // Most recent failed run date (only if more recent than latest success)
  isRefreshing: boolean;               // True if any job is currently in PROCESSING status (actively running)
  isPartial: boolean;                  // True if some (but not all) job types have succeeded at least once
}

export interface ClientWithScrapingTimes {
  id: string;
  name: string | null;
  panId: string;
  email?: string | null;
  password: string;
  updatedAt: string;
  phone?: string | null;
  clientType?: ClientType;
  businessType?: 'AUDITED' | 'NON_AUDITED';
  loginStatus?: 'SUCCESSFUL' | 'INCORRECT_USERNAME' | 'INCORRECT_PASSWORD' | 'PAN_NOT_EXISTING' | null;
  isVerified?: boolean;
  dateOfBirth?: string | null;
  gender?: string | null;
  panStatus?: string | null;
  aadhaarLinked?: boolean | null;
  address?: string | null;
  profileLastSyncedAt?: string | null;
  loginEmail?: string | null;
  loginEnabled?: boolean;
  loginEnabledAt?: string | null;
  loginInvitedAt?: string | null;
  clientLastLoginAt?: string | null;
  scrapingTimes: Array<{
    jobId: string;
    jobType: 'FYA' | 'DEMANDS' | 'ITR' | 'FYI' | 'AIS_TIS';
    lastSuccessfulRun: string | null;
    lastRunStatus: 'SUCCESS' | 'FAILED' | 'PROCESSING' | 'PENDING' | null;
    lastRunDate: string | null;
  }>;
  refreshStatus?: RefreshStatus;
}

export interface GetClientsResponse {
  status: 'success' | 'error';
  clients: ClientWithScrapingTimes[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  permissions: {
    canAddClients: boolean;
    currentClientCount: number;
    clientLimit: number;
    isSubscriptionActive: boolean;
    subscriptionType: string | undefined;
  };
  error?: string;
}

const axiosInstance = axios.create({
  baseURL: '/api/proxy/',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a response interceptor to directly return data
axiosInstance.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // You can handle errors globally here if needed, or just re-throw
    return Promise.reject(error);
  },
);

// Define request interfaces
export interface CreateClientRequest {
  name: string;
  panId: string; // Backend expects panId, not pan
  email?: string;
  phone?: string;
  password: string;
  clientType: ClientType;
  businessType: 'AUDITED' | 'NON_AUDITED';
  // serviceIds are now handled automatically on the backend
}

export interface CreateClientResponse {
  message: string;
  client: Client;
  status: string;
}

export interface UpdateClientResponse {
  message: string;
  client: Client;
  status: string;
}

export interface FailedRecord {
  row: number;
  name: string;
  panId: string;
  error: string;
}

export interface BulkImportResponse {
  message: string;
  summary: {
    total: number;
    successful: number;
    failed: number;
  };
  failedRecords: FailedRecord[];
  status: string;
}

export const clientService = {
  getServices: async (): Promise<GetServicesResponse> => {
    return axiosInstance.get('clients/clients/services');
  },

  getClients: async ({
    page,
    limit,
    search,
    clientType,
    verificationFailed,
    refreshFailed,
    verifiedRefreshFailed,
  }: {
    page: number;
    limit: number;
    search?: string;
    clientType?: ClientType;
    verificationFailed?: boolean;
    refreshFailed?: boolean;
    verifiedRefreshFailed?: boolean;
  }): Promise<GetClientsResponse> => {
    const params: Record<string, string | number> = { page, limit };
    if (search) params.search = search;
    if (clientType) params.clientType = clientType;
    if (verificationFailed) params.verificationFailed = 'true';
    if (refreshFailed) params.refreshFailed = 'true';
    if (verifiedRefreshFailed) params.verifiedRefreshFailed = 'true';
    return axiosInstance.get('clients/clients', { params });
  },

  /**
   * Fetch a single client. Note: unlike the list endpoint, this does not include
   * `scrapingTimes` / `refreshStatus`; callers that need sync status should
   * look the client up in the list (e.g. by PAN) after resolving it here.
   */
  getClientById: async (clientId: string): Promise<{ client: Client }> => {
    return axiosInstance.get(`clients/clients/${encodeURIComponent(clientId)}`);
  },

  addClient: async (client: Omit<Client, 'id' | 'updatedAt'> & { businessType: 'AUDITED' | 'NON_AUDITED'; clientType: ClientType }): Promise<CreateClientResponse> => {
    // Transform the client data to match the backend API expectations
    const clientRequest: CreateClientRequest = {
      name: client.name,
      panId: client.panId,
      email: client.email, // Email is now optional
      phone: client.phone, // Add phone field
      password: client.password, // Password is required and non-optional in our interface
      clientType: client.clientType,
      businessType: client.businessType,
    };

    return axiosInstance.post('clients/clients', clientRequest);
  },

  bulkImportClients: async (file: File): Promise<BulkImportResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axiosInstance.post('clients/clients/bulk-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response as unknown as BulkImportResponse;
    } catch (error: unknown) {
      // Type guard for axios error structure
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'data' in error.response
      ) {
        const errorData = error.response.data as { error?: string };
        throw new Error(errorData.error || 'Failed to import clients');
      }
      throw new Error('Failed to import clients');
    }
  },

  async deleteClient(clientId: string): Promise<{ message: string }> {
    return axiosInstance.delete(`clients/clients/${clientId}`);
  },

  updateClient: async (
    clientId: string,
    clientData: Partial<Omit<Client, 'id' | 'updatedAt'>>,
  ): Promise<UpdateClientResponse> => {
    // Format phone number if it exists
    const formattedData = {
      ...clientData,
      phone: clientData.phone ? clientData.phone.replace(/^\+91/, '') : undefined,
    };

    return axiosInstance.put(`clients/clients/${clientId}`, formattedData);
  },

  /**
   * Enable or disable portal login for a client. Enabling sends the client an
   * invite email at the given address.
   */
  updateLoginAccess: async (
    clientId: string,
    payload: { enabled: boolean; email?: string },
  ): Promise<UpdateClientLoginAccessResponse> => {
    return axiosInstance.put(`clients/clients/${clientId}/login-access`, payload);
  },
};

export interface UpdateClientLoginAccessResponse {
  message: string;
  inviteSent: boolean;
  client: Client & {
    loginEmail?: string | null;
    loginEnabled?: boolean;
    loginEnabledAt?: string | null;
    loginInvitedAt?: string | null;
    clientLastLoginAt?: string | null;
  };
}
